import type { AxiosRequestConfig } from 'axios';

type ErrorKind = 'network' | 'server' | 'timeout' | 'bad-gateway';

export interface GlobalErrorPayload {
  statusCode?: number;
  message?: string;
  errorType: ErrorKind;
  retryAction?: () => Promise<void>;
  retryUrl?: string;
  previousUrl?: string;
  requestConfig?: AxiosRequestConfig;
}

type GlobalErrorListener = (payload: GlobalErrorPayload) => void;

const listeners = new Set<GlobalErrorListener>();

export interface PersistedErrorContext {
  errorType: ErrorKind;
  previousUrl?: string;
  retryUrl?: string;
  message?: string;
  statusCode?: number;
  timestamp: number;
  requestInfo?: {
    url?: string;
    method?: string;
    baseURL?: string;
  };
}

const ERROR_STORAGE_KEY = 'error_context';

const persistErrorContext = (payload: GlobalErrorPayload): void => {
  const { requestConfig } = payload;
  const requestInfo = requestConfig
    ? {
        url: requestConfig.url,
        method: requestConfig.method,
        baseURL: requestConfig.baseURL,
      }
    : undefined;

  const contextToPersist: PersistedErrorContext = {
    errorType: payload.errorType,
    previousUrl: payload.previousUrl,
    retryUrl: payload.retryUrl,
    message: payload.message,
    statusCode: payload.statusCode,
    timestamp: Date.now(),
    requestInfo,
  };

  try {
    localStorage.setItem(ERROR_STORAGE_KEY, JSON.stringify(contextToPersist));
  } catch (error) {
    console.error('Failed to persist error context', error);
  }
};

export const emitGlobalError = (payload: GlobalErrorPayload): void => {
  persistErrorContext(payload);
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('Global error listener failed', error);
    }
  });
};

export const subscribeToGlobalErrors = (listener: GlobalErrorListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
